import React from "react";
import { Tag, Tooltip } from "antd";
import { Link } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import IssueKeyBadge from "../../issues/components/IssueKeyBadge";
import UserAvatar from "../../components/UserAvatar";
import { ISSUE_TYPES, PRIORITY_OPTIONS } from "../../constants/projects.constants";
import { all_routes } from "../../../router/all_routes";

import type { Issue } from "../../../../core/data/redux/projects/types";

interface BoardIssueCardProps { issue: Issue; projectKey: string; }

const BoardIssueCard: React.FC<BoardIssueCardProps> = ({ issue, projectKey }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `issue-${issue.id}`, data: { issueId: issue.id } });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1, cursor: isDragging ? "grabbing" : "grab", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", userSelect: "none", WebkitUserSelect: "none", touchAction: "none" };
  const typeMeta = ISSUE_TYPES.find((t) => t.value === issue.type);
  const priorityMeta = PRIORITY_OPTIONS.find((p) => p.value === issue.priority);
  const detailPath = all_routes.projectIssueDetails.replace(":projectKey", projectKey).replace(":issueKey", issue.issue_key);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className="card mb-2 border-0 overflow-hidden"
      onMouseEnter={(e) => {
        if (isDragging) return;
        e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.10)";
      }}
      onMouseLeave={(e) => {
        if (isDragging) return;
        e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)";
      }}
    >
      <div style={{ height: 3, background: typeMeta?.color || "#6B7280" }} />
      <div className="card-body bg-white rounded-3" style={{ padding: "10px 12px 8px" }}>
        <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
          <Link to={detailPath} className="text-decoration-none text-dark fw-semibold flex-grow-1" onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} style={{ fontSize: 13, lineHeight: 1.4 }}>{issue.title}</Link>
          {priorityMeta && (
            <Tooltip title={`Priority: ${priorityMeta.label}`}>
              <span className="d-inline-flex align-items-center gap-1" style={{ background: "#f8fafc", padding: "2px 6px", borderRadius: 4, fontSize: 10, color: priorityMeta.color, fontWeight: 600, flexShrink: 0 }}>
                <span className="d-inline-block rounded-circle" style={{ width: 6, height: 6, background: priorityMeta.color }} />{priorityMeta.label}
              </span>
            </Tooltip>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 small text-muted mb-2">
          {typeMeta && (
            <Tooltip title={`Type: ${typeMeta.label}`}>
              <span className="d-inline-flex align-items-center gap-1" style={{ color: typeMeta.color, fontSize: 11 }}>
                <span className="d-inline-block rounded-circle" style={{ width: 6, height: 6, background: typeMeta.color }} />{typeMeta.label}
              </span>
            </Tooltip>
          )}
          <IssueKeyBadge projectKey={projectKey} issueKey={issue.issue_key} className="ms-auto" />
        </div>
        {((issue.labels && issue.labels.length > 0) || issue.due_date) && (
          <div className="d-flex flex-wrap align-items-center gap-1 mb-1">
            {(issue.labels || []).slice(0, 3).map((l) => (<Tag key={l.id} color={l.color || "default"} style={{ marginRight: 0, fontSize: 10, lineHeight: "16px" }}>{l.name}</Tag>))}
            {issue.due_date && (<span className="text-muted small ms-auto" style={{ fontSize: 10.5 }}><i className="ti ti-calendar me-1" />{new Date(issue.due_date).toLocaleDateString()}</span>)}
          </div>
        )}
        {issue.assignee_snapshot?.name && (
          <div className="d-flex align-items-center gap-2 mt-1 pt-1" style={{ borderTop: "1px solid #f1f5f9" }}>
            <UserAvatar name={issue.assignee_snapshot.name} img={issue.assignee_snapshot.img} size={20} />
            <span className="small text-muted" style={{ fontSize: 11 }}>{issue.assignee_snapshot.name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoardIssueCard;
